import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, CheckCircle, Lock, Eye, ShieldCheck, AlertTriangle, ArrowRight, Phone, FileCheck } from 'lucide-react';

export default function Seguridad() {
  const caracteristicas = [
    {
      icon: ShieldCheck,
      titulo: 'Zero Trust Architecture',
      descripcion: 'Implementación de seguridad basada en verificación continua y privilegio mínimo.'
    },
    {
      icon: Eye,
      titulo: 'Visibility & Detection',
      descripcion: 'Monitoreo 24/7 de amenazas con detección avanzada y respuesta automatizada.'
    },
    {
      icon: Lock,
      titulo: 'Data Protection',
      descripcion: 'Cifrado, clasificación y control de acceso para datos críticos del negocio.'
    },
    {
      icon: FileCheck,
      titulo: 'Compliance & Governance',
      descripcion: 'Cumplimiento automatizado de regulaciones (ISO 27001, PCI-DSS, GDPR, HIPAA).'
    }
  ];

  const pilares = [
    {
      titulo: 'Security by Design',
      descripcion: 'HPE GreenLake integra seguridad desde el firmware (Silicon Root of Trust) hasta las aplicaciones.',
      detalles: [
        'Firmware firmado criptográficamente',
        'Secure boot y attestation',
        'Actualizaciones de seguridad automatizadas',
        'Aislamiento de workloads críticos'
      ]
    },
    {
      titulo: 'Identity & Access Management',
      descripcion: 'Control granular de identidades con autenticación multifactor y SSO empresarial.',
      detalles: [
        'MFA adaptativo basado en riesgo',
        'Single Sign-On (SSO) con SAML/OIDC',
        'Privileged Access Management (PAM)',
        'Role-Based Access Control (RBAC)'
      ]
    },
    {
      titulo: 'Threat Detection & Response',
      descripcion: 'Detección proactiva de amenazas con inteligencia artificial y respuesta automatizada.',
      detalles: [
        'SIEM con correlación de eventos',
        'EDR (Endpoint Detection & Response)',
        'Threat intelligence feeds',
        'Automated incident response'
      ]
    },
    {
      titulo: 'Data Security & Privacy',
      descripcion: 'Protección integral de datos en reposo, tránsito y uso con cifrado y DLP.',
      detalles: [
        'Cifrado AES-256 en múltiples capas',
        'Data Loss Prevention (DLP)',
        'Clasificación automática de datos',
        'Tokenización y enmascaramiento'
      ]
    },
    {
      titulo: 'Network Security',
      descripcion: 'Segmentación de red, micro-segmentación y protección perimetral avanzada.',
      detalles: [
        'Firewalls de próxima generación',
        'Segmentación con VLANs y VXLANs',
        'IPS/IDS con aprendizaje automático',
        'VPNs site-to-site y remote access'
      ]
    },
    {
      titulo: 'Compliance & Audit',
      descripcion: 'Frameworks de cumplimiento y auditoría continua para regulaciones globales.',
      detalles: [
        'Templates para ISO 27001, PCI-DSS, GDPR',
        'Auditoría continua automatizada',
        'Reportes de compliance ejecutivos',
        'Evidencia para auditorías externas'
      ]
    }
  ];

  const servicios = [
    {
      titulo: 'Security Assessment',
      descripcion: 'Evaluación completa de tu postura de seguridad actual con recomendaciones priorizadas',
      entregables: ['Vulnerability assessment', 'Penetration testing', 'Gap analysis vs best practices', 'Roadmap de remediación']
    },
    {
      titulo: 'Security Operations Center (SOC)',
      descripcion: 'Monitoreo 24/7/365 de tu infraestructura con analistas especializados',
      entregables: ['Monitoreo continuo de eventos', 'Triage y escalamiento de incidentes', 'Threat hunting proactivo', 'Reportes semanales/mensuales']
    },
    {
      titulo: 'Incident Response',
      descripcion: 'Respuesta rápida ante brechas de seguridad con contención y remediación',
      entregables: ['Análisis forense digital', 'Contención de amenazas', 'Erradicación y recuperación', 'Post-incident review']
    }
  ];

  const beneficios = [
    'Reducción de 85% en tiempo de detección de amenazas con SIEM + IA',
    'Cumplimiento automatizado de regulaciones (ISO 27001, PCI-DSS, GDPR)',
    'Protección desde el firmware con Silicon Root of Trust',
    'Visibilidad completa de activos y vulnerabilidades en tiempo real',
    'Respuesta a incidentes en minutos vs horas con automatización',
    'Reducción de riesgo de brechas de datos en 70%',
    'Auditorías simplificadas con evidencia automática'
  ];

  return (
    <div className="bg-white">
      {/* Hero Section */}
      <section 
        className="relative py-20 bg-cover bg-center"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1555949963-ff9fe0c870eb?q=80&w=2370)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-red-900/90 via-gray-900/85 to-red-900/90"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="inline-block bg-red-600/20 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
              <span className="text-red-300 font-semibold">HPE GreenLake Security Adoption</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              Seguridad Empresarial Integral
            </h1>
            <p className="text-xl md:text-2xl text-gray-200 mb-8">
              Protección multicapa desde el firmware hasta la nube con Zero Trust y cumplimiento automatizado
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link 
                to="/contacto" 
                className="bg-red-600 text-white px-8 py-4 rounded-lg hover:bg-red-700 transition-colors text-lg font-semibold inline-flex items-center justify-center"
              >
                Security Assessment Gratis <ArrowRight className="ml-2" />
              </Link>
              <a 
                href="#pilares" 
                className="bg-white/10 backdrop-blur-sm text-white px-8 py-4 rounded-lg hover:bg-white/20 transition-colors text-lg font-semibold inline-flex items-center justify-center border border-white/30"
              >
                Ver Pilares de Seguridad
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Introducción */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Seguridad no es opcional, es fundamental
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                Los <strong>ciberataques</strong> cuestan millones a empresas cada año. Ransomware, 
                brechas de datos, y ataques de día cero son amenazas reales. La <strong>seguridad 
                empresarial</strong> moderna requiere un enfoque holístico: desde el hardware (firmware) 
                hasta las aplicaciones y datos.
              </p>
              <p className="text-lg text-gray-600 mb-6">
                Con <strong>HPE GreenLake Security Adoption</strong>, implementamos arquitectura 
                <strong> Zero Trust</strong>, monitoreo continuo 24/7, y cumplimiento automatizado de 
                regulaciones como ISO 27001, PCI-DSS, GDPR y HIPAA. Tu infraestructura protegida desde 
                la raíz.
              </p>
              <div className="bg-red-50 border-l-4 border-red-600 p-6 rounded-lg">
                <p className="text-gray-800 font-semibold mb-2 flex items-center">
                  <AlertTriangle className="inline mr-2 text-red-600" size={24} />
                  El costo promedio de una brecha de datos
                </p>
                <p className="text-gray-600">
                  Según IBM Security, el costo promedio de una brecha de datos en 2024 es de 
                  <strong> $4.45 millones USD</strong>. Las empresas tardan en promedio 277 días 
                  en identificar y contener una brecha. La prevención es más económica que la remediación.
                </p>
              </div>
            </div>
            <div>
              <img 
                src="https://images.unsplash.com/photo-1563013544-824ae1b704d3?q=80&w=2070" 
                alt="Cybersecurity"
                className="rounded-xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Características */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Componentes Clave
            </h2>
            <p className="text-xl text-gray-600">
              Seguridad integral en todas las capas
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {caracteristicas.map((item, index) => {
              const Icon = item.icon;
              return (
                <div key={index} className="bg-white p-8 rounded-xl shadow-lg hover:shadow-2xl transition-shadow">
                  <div className="bg-red-50 w-16 h-16 rounded-lg flex items-center justify-center mb-6">
                    <Icon className="text-red-600" size={32} />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{item.titulo}</h3>
                  <p className="text-gray-600">{item.descripcion}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Pilares */}
      <section id="pilares" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              6 Pilares de Seguridad
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Framework completo de protección empresarial
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {pilares.map((pilar, index) => (
              <div 
                key={index}
                className="bg-gradient-to-br from-gray-50 to-white border border-red-200 rounded-xl p-8 hover:shadow-xl transition-all"
              >
                <div className="flex items-center space-x-3 mb-4">
                  <div className="bg-red-100 p-3 rounded-lg">
                    <Shield className="text-red-600" size={24} />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">{pilar.titulo}</h3>
                </div>
                <p className="text-gray-600 mb-4">{pilar.descripcion}</p>
                <ul className="space-y-2">
                  {pilar.detalles.map((detalle, idx) => (
                    <li key={idx} className="flex items-start space-x-2 text-sm text-gray-600">
                      <CheckCircle className="text-hpe-green flex-shrink-0 mt-0.5" size={16} />
                      <span>{detalle}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Servicios */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Servicios de Seguridad Gestionados
            </h2>
            <p className="text-xl text-gray-600">
              Protección continua con expertos certificados
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {servicios.map((servicio, index) => (
              <div key={index} className="bg-white p-8 rounded-xl shadow-lg">
                <h3 className="text-2xl font-bold text-gray-900 mb-3">{servicio.titulo}</h3>
                <p className="text-gray-600 mb-6">{servicio.descripcion}</p>
                <div className="border-t border-gray-200 pt-4">
                  <p className="text-sm font-semibold text-gray-700 mb-3">Incluye:</p>
                  <ul className="space-y-2">
                    {servicio.entregables.map((entregable, idx) => (
                      <li key={idx} className="flex items-start space-x-2 text-sm text-gray-600">
                        <CheckCircle className="text-hpe-green flex-shrink-0 mt-0.5" size={14} />
                        <span>{entregable}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Beneficios */}
      <section className="py-20 bg-gradient-to-br from-red-600 to-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="text-white">
              <h2 className="text-4xl font-bold mb-6">
                Resultados Comprobados
              </h2>
              <p className="text-xl text-red-100 mb-8">
                Organizaciones que implementan seguridad integral con HPE experimentan:
              </p>
              <ul className="space-y-4">
                {beneficios.map((beneficio, index) => (
                  <li key={index} className="flex items-start space-x-3">
                    <CheckCircle className="text-hpe-green flex-shrink-0 mt-1" size={24} />
                    <span className="text-lg text-red-50">{beneficio}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <img 
                src="https://images.unsplash.com/photo-1614064641938-3bbee52942c7?q=80&w=2070" 
                alt="Security Operations"
                className="rounded-xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Shield className="text-red-600 mx-auto mb-6" size={64} />
          <h2 className="text-4xl font-bold text-gray-900 mb-6">
            Protege tu empresa hoy
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            ALLTI implementa arquitecturas de seguridad Zero Trust con HPE GreenLake. Incluye 
            assessment gratuito de tu postura de seguridad actual.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/contacto" 
              className="bg-red-600 text-white px-8 py-4 rounded-lg hover:bg-red-700 transition-colors text-lg font-semibold inline-flex items-center justify-center"
            >
              <Phone className="mr-2" size={20} />
              Solicitar Security Assessment
            </Link>
            <Link 
              to="/casos-de-uso" 
              className="bg-gray-100 text-gray-900 px-8 py-4 rounded-lg hover:bg-gray-200 transition-colors text-lg font-semibold inline-flex items-center justify-center"
            >
              Ver Casos de Éxito
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
